import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorState, LoadingState, StaleBanner } from './AsyncState';
import styles from './DataTable.module.css';

export interface Column<T> {
  key: string;
  header: string;
  width?: string;
  render: (row: T) => React.ReactNode;
}

interface Props<T> {
  columns: Column<T>[];
  rows: T[];
  keyFn: (row: T) => string | number;
  empty?: string;
  loading?: boolean;
  error?: string;
  onRetry?: () => unknown;
  /** Makes the whole row clickable (cursor + hover). Inner links/buttons
   *  still work — clicks on them are not hijacked. */
  onRowClick?: (row: T) => void;
}

export function DataTable<T>({
  columns,
  rows,
  keyFn,
  empty = 'No data',
  loading,
  error,
  onRetry,
  onRowClick,
}: Props<T>) {
  const { t } = useTranslation();
  const [retryError, setRetryError] = useState<string>();
  const visibleError = error ?? retryError;
  async function retry() {
    if (!onRetry) return;
    setRetryError(error);
    try { return await onRetry(); }
    finally { setRetryError(undefined); }
  }
  return (
    <div className={styles.wrapper}>
      {visibleError && rows.length > 0 && <StaleBanner onRetry={onRetry ? retry : undefined} />}
      <table className={styles.table}>
        <thead>
          <tr>
            {columns.map((col) => (
              <th key={col.key} style={col.width ? { width: col.width } : undefined}>
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className={rows.length > 0 ? styles.reveal : undefined}>
          {visibleError && rows.length === 0 ? (
            <tr><td colSpan={columns.length}><ErrorState title={visibleError} onRetry={onRetry ? retry : undefined} /></td></tr>
          ) : loading && rows.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className={styles.placeholder}>
                <LoadingState label={t('ui.dataTable.loading')} />
              </td>
            </tr>
          ) : rows.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className={styles.placeholder}>
                {empty}
              </td>
            </tr>
          ) : (
            rows.map((row) => (
              <tr
                key={keyFn(row)}
                className={onRowClick ? styles.clickableRow : undefined}
                onClick={
                  onRowClick
                    ? (e) => {
                        // Don't hijack clicks on inner links/buttons/inputs
                        const target = e.target as HTMLElement;
                        if (target.closest('a, button, input, textarea, select')) return;
                        onRowClick(row);
                      }
                    : undefined
                }
              >
                {columns.map((col) => (
                  <td key={col.key}>{col.render(row)}</td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
